import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import Navbar from './components/Navbar';
import DriverLayout from './components/DriverLayout';
import RiderLayout from './components/RiderLayout';
import AdminLayout from './components/AdminLayout';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PhoneVerification from './pages/PhoneVerification';
import EmailVerification from './pages/EmailVerification';
import VerificationSuccess from './pages/VerificationSuccess';
import PendingApproval from './pages/auth/PendingApproval';
import PendingDrivers from './pages/admin/PendingDrivers';
import DriverProfiles from './pages/admin/DriverProfiles';
import RiderProfiles from './pages/admin/RiderProfiles';
import RiderDashboard from './pages/rider/Dashboard';
import DriverDashboard from './pages/driver/Dashboard';
import AdminDashboard from './pages/admin/Dashboard';
import UsersManagement from './pages/admin/UsersManagement';
import RidesManagement from './pages/admin/RidesManagement';
import UnmatchedRequests from './pages/admin/UnmatchedRequests';
import BookingsManagement from './pages/admin/BookingsManagement';
import ManualMatching from './pages/admin/ManualMatching';
import AdminProfiles from './pages/admin/AdminProfiles';
import AnalyticsReports from './pages/admin/AnalyticsReports';
import AppSettings from './pages/admin/AppSettings';
import VehicleManagement from './pages/driver/VehicleManagement';
import RideManagement from './pages/driver/RideManagement';
import BookRide from './pages/rider/BookRide';
import MyBookings from './pages/rider/MyBookings';
import AvailableRides from './pages/rider/AvailableRides';
import RideBookings from './pages/driver/RideBookings';
import RideRequests from './pages/driver/RideRequests';
import Profile from './pages/driver/Profile';
import RiderProfile from './pages/rider/Profile';
import NotFound from './pages/NotFound';

// Layout component that conditionally renders the Navbar
function AppLayout() {
  const location = useLocation();
  
  // Check if we're on an admin route
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  // Check if we're on a rider route
  const isRiderRoute = location.pathname.startsWith('/rider');
  
  // Check if we're on a driver route
  const isDriverRoute = location.pathname.startsWith('/driver');
  
  // Check if we're on an auth route (login/register)
  const isAuthRoute = location.pathname === '/login' || 
                     location.pathname === '/register' || 
                     location.pathname === '/forgot-password' || 
                     location.pathname === '/reset-password' ||
                     location.pathname === '/verify-phone' ||
                     location.pathname === '/verify-email' ||
                     location.pathname === '/verification-success' ||
                     location.pathname === '/pending-approval';
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Only show the main navbar if we're not on admin, rider, driver or auth routes */}
      {!isAdminRoute && !isRiderRoute && !isDriverRoute && !isAuthRoute && <Navbar />}
      
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-phone" element={<PhoneVerification />} />
        <Route path="/verify-email/:token" element={<EmailVerification />} />
        <Route path="/verification-success" element={<VerificationSuccess />} />
        <Route path="/pending-approval" element={<PendingApproval />} />

        {/* Rider Routes */}
        <Route
          path="/rider/dashboard"
          element={
            <PrivateRoute userType="rider">
              <RiderLayout>
                <RiderDashboard />
              </RiderLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/rider/available-rides"
          element={
            <PrivateRoute userType="rider">
              <RiderLayout>
                <AvailableRides />
              </RiderLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/rider/book-ride"
          element={
            <PrivateRoute userType="rider">
              <RiderLayout>
                <BookRide />
              </RiderLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/rider/my-bookings"
          element={
            <PrivateRoute userType="rider">
              <RiderLayout>
                <MyBookings />
              </RiderLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/rider/profile"
          element={
            <PrivateRoute userType="rider">
              <RiderLayout>
                <RiderProfile />
              </RiderLayout>
            </PrivateRoute>
          }
        />

        {/* Driver Routes */}
        {/* Legacy/back-end links used /driver/requests; SPA route is ride-requests */}
        <Route path="/driver/requests" element={<Navigate to="/driver/ride-requests" replace />} />
        <Route
          path="/driver/dashboard"
          element={
            <PrivateRoute userType="driver">
              <DriverLayout>
                <DriverDashboard />
              </DriverLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/driver/vehicles"
          element={
            <PrivateRoute userType="driver">
              <DriverLayout>
                <VehicleManagement />
              </DriverLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/driver/rides"
          element={
            <PrivateRoute userType="driver">
              <DriverLayout>
                <RideManagement />
              </DriverLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/driver/ride-requests"
          element={
            <PrivateRoute userType="driver">
              <DriverLayout>
                <RideRequests />
              </DriverLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/driver/bookings"
          element={
            <PrivateRoute userType="driver">
              <DriverLayout>
                <RideBookings />
              </DriverLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/driver/profile"
          element={
            <PrivateRoute userType="driver">
              <DriverLayout>
                <Profile />
              </DriverLayout>
            </PrivateRoute>
          }
        />

        {/* Admin Routes */}
        <Route
          path="/admin/dashboard"
          element={
            <PrivateRoute userType="admin">
              <AdminLayout>
                <AdminDashboard />
              </AdminLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <PrivateRoute userType="admin">
              <AdminLayout>
                <UsersManagement />
              </AdminLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/drivers"
          element={
            <PrivateRoute userType="admin">
              <AdminLayout>
                <DriverProfiles />
              </AdminLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/riders"
          element={
            <PrivateRoute userType="admin">
              <AdminLayout>
                <RiderProfiles />
              </AdminLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/admins"
          element={
            <PrivateRoute userType="admin">
              <AdminLayout>
                <AdminProfiles />
              </AdminLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/rides"
          element={
            <PrivateRoute userType="admin">
              <AdminLayout>
                <RidesManagement />
              </AdminLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/unmatched-requests"
          element={
            <PrivateRoute userType="admin">
              <AdminLayout>
                <UnmatchedRequests />
              </AdminLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/manual-matching"
          element={
            <PrivateRoute userType="admin">
              <AdminLayout>
                <ManualMatching />
              </AdminLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/bookings"
          element={
            <PrivateRoute userType="admin">
              <AdminLayout>
                <BookingsManagement />
              </AdminLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/analytics"
          element={
            <PrivateRoute userType="admin">
              <AdminLayout>
                <AnalyticsReports />
              </AdminLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <PrivateRoute userType="admin">
              <AdminLayout>
                <AppSettings />
              </AdminLayout>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/pending-drivers"
          element={
            <PrivateRoute userType="admin">
              <AdminLayout>
                <PendingDrivers />
              </AdminLayout>
            </PrivateRoute>
          }
        />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </div>
  );
}

function App() {
  const basename = (process.env.PUBLIC_URL || '').replace(/\/$/, '');

  return (
    <AuthProvider>
      <Router basename={basename || undefined}>
        <AppLayout />
      </Router>
    </AuthProvider>
  );
}

export default App;
